import { LoadingContainer } from 'peach/components'
import { useHistory, useParams } from 'peach/router'

import { DynamicForm, Page, PageHeader } from 'core/components'
import { useCreateWorkflowStepType, useGetWorkitemTypes } from 'core/types'
import {
  workflowStepTypeSchema,
  type StaticFieldSchema,
} from 'core/utils/generated/formSchemas'

const CreateWorkflowStepType = () => {
  const history = useHistory()
  const { companyId } = useParams<{ companyId?: string }>()

  const { mutateAsync: createWorkflowStepType } = useCreateWorkflowStepType({
    companyId,
  })

  const { data: workitemTypes } = useGetWorkitemTypes({ companyId })

  const workItemOptions: Array<{
    label: string
    value: string
    sublabel?: string
  }> = Array.isArray(workitemTypes)
    ? (workitemTypes as Array<any>)
        .map((w) => {
          const id = w?.id as string | undefined
          if (!id) return null
          const desc = (w?.descriptionShort ||
            w?.descriptionLong ||
            '') as string
          const truncated = desc
            ? desc.length > 70
              ? desc.slice(0, 70) + '…'
              : desc
            : undefined
          return truncated
            ? { label: id, value: id, sublabel: truncated }
            : { label: id, value: id }
        })
        .filter(
          (v): v is { label: string; value: string; sublabel?: string } =>
            v !== null,
        )
    : []

  // Inject options into the top-level 'workItems' field so MultiSelect can render choices
  const enhancedSchema: Array<StaticFieldSchema> = (
    workflowStepTypeSchema as Array<StaticFieldSchema>
  ).map((f) => (f.key === 'workItems' ? { ...f, options: workItemOptions } : f))

  return (
    <LoadingContainer isLoading={false}>
      <Page variant='full'>
        <PageHeader title='Create Workflow Step Type' />
        <DynamicForm
          data={{}}
          fieldEditMode='all'
          fieldOverrides={{ workItems: 'MultiSelect' }}
          onSubmit={async (payload: any) => {
            const mappedWorkItems = Array.isArray(payload?.workItems)
              ? (payload.workItems as Array<any>).map((it) =>
                  typeof it === 'string' ? it : it?.id ?? it?.value ?? it,
                )
              : payload?.workItems

            const res = await createWorkflowStepType({
              ...payload,
              workItems: mappedWorkItems,
            })
            const id = (res as any)?.data?.id as string | undefined
            if (id) {
              history.push(`/companies/${companyId}/workflow-step-types/${id}`)
            } else {
              history.push(`/companies/${companyId}/workflow-step-types`)
            }
          }}
          schema={enhancedSchema}
          submitText='Create'
          validationRules={{
            descriptionShort: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'descriptionShort is required',
            ],
          }}
        />
      </Page>
    </LoadingContainer>
  )
}

export default CreateWorkflowStepType
